// Courses on the order pad — the same map as backend/kitchen.py `course_for`, so what the
// server writes on a line and what the pad groups by never disagree. A line that has
// been priced carries `course` from the server; a line that hasn't been sent yet is
// grouped from its menu category here.

export const COURSE_ORDER = ['starters', 'mains', 'desserts', 'drinks', 'other'];
export const COURSE_LABEL = { starters: 'Starters', mains: 'Mains', desserts: 'Desserts', drinks: 'Drinks', other: 'Other' };

const DRINKS = new Set([
  'lagers', 'wines', 'cocktails', 'soft_drinks', 'drinks', 'kids_drinks', 'shakes', 'beverages',
  'wine', 'beer', 'beers', 'ales', 'cider', 'ciders', 'beer_and_cider', 'beers_and_ciders',
  'spirits', 'mocktails', 'juices', 'hot_drinks', 'coffee', 'coffees', 'tea', 'teas',
]);
const BY_CATEGORY = {
  starters: 'starters', starter: 'starters', small_plates: 'starters', sharers: 'starters',
  mains: 'mains', main: 'mains', sides: 'mains', side: 'mains', kids: 'mains', burgers: 'mains',
  kids_starters: 'starters', kids_mains: 'mains', kids_desserts: 'desserts',
  desserts: 'desserts', dessert: 'desserts', puddings: 'desserts',
  nibbles: 'starters', snacks: 'starters', sharing: 'starters', to_share: 'starters', soups: 'starters',
  roasts: 'mains', sunday_roasts: 'mains', grill: 'mains', from_the_grill: 'mains', pizzas: 'mains',
  pasta: 'mains', pudding: 'desserts', sweets: 'desserts',
};

// As backend/kitchen.py `category_key`: the back office stores a section's name ("Small
// plates", "Beer & cider"), the demo menu its key ("small_plates").
export const categoryKey = (category) => String(category || '').toLowerCase()
  .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export const courseForCategory = (category) => {
  const c = categoryKey(category);
  if (DRINKS.has(c)) return 'drinks';
  return BY_CATEGORY[c] || 'other';
};

export const courseOf = (line) => line.course || courseForCategory(line.category);

// [{ course, label, lines: [{ line, index }] }] in service order, only the courses present.
export const groupByCourse = (lines) => {
  const groups = new Map();
  lines.forEach((line, index) => {
    const c = courseOf(line);
    if (!groups.has(c)) groups.set(c, []);
    groups.get(c).push({ line, index });
  });
  return COURSE_ORDER.filter((c) => groups.has(c)).map((c) => ({ course: c, label: COURSE_LABEL[c], lines: groups.get(c) }));
};

// A sent line's progress, from the ticket that carries it. `tickets` are the order's own
// (any station). A line the kitchen has struck off reads as voided.
export const lineProgress = (line, tickets) => {
  if (!line.line_id) return 'new';
  for (const t of tickets || []) {
    const hit = (t.items || []).find((l) => l.line_id === line.line_id);
    if (hit) {
      if (hit.voided) return 'voided';
      return { pending: 'sent', in_progress: 'cooking', ready: 'ready', delivered: 'served' }[t.status] || 'sent';
    }
  }
  return 'sent';
};

export const PROGRESS = {
  new:     { word: 'new',     rail: 'bg-signal-warn-line',   text: 'text-signal-warn' },
  sent:    { word: 'sent',    rail: 'bg-ink-faint',   text: 'text-ink-soft' },
  cooking: { word: 'cooking', rail: 'bg-signal-info-line',    text: 'text-signal-info' },
  ready:   { word: 'ready',   rail: 'bg-signal-ok-line', text: 'text-signal-ok' },
  served:  { word: 'served',  rail: 'bg-hairline',   text: 'text-ink-faint' },
  voided:  { word: 'voided',  rail: 'bg-signal-stop-line',    text: 'text-signal-stop' },
};

/** Lines already with the kitchen whose message has been changed on the pad but not yet
 *  sent. Without this the verb stays "Bill" and "actually, no peanuts" sits on the screen
 *  looking done while the kitchen has never been told. */
export const amendedCount = (cart = [], orderItems = []) => {
  const sent = new Map((orderItems || []).filter((l) => l.line_id).map((l) => [l.line_id, l]));
  const said = (l) => `${(l?.notes || '').trim()}\u0000${(l?.allergy_notes || '').trim()}`;
  return (cart || []).filter((l) => l.line_id && sent.has(l.line_id)
    && said(l) !== said(sent.get(l.line_id))).length;
};

// The one thing the bottom of the pad offers.
export const primaryVerb = ({ newCount, hasOrder, remaining, paid, mode = 'table', amended = 0 }) => {
  // A bar order has no table and no send: the drinks are made as they're paid for.
  if (mode === 'bar') {
    return newCount > 0 ? { key: 'pay', label: 'Take payment', amount: remaining } : { key: 'none', label: 'Add drinks to start the order' };
  }
  if (newCount > 0) return { key: 'send', label: `Send ${newCount} item${newCount === 1 ? '' : 's'}` };
  // An amendment is a send too: the board updates and a slip prints where a chef has started.
  if (amended > 0) return { key: 'send', label: `Tell the kitchen${amended > 1 ? ` (${amended})` : ''}` };
  if (!hasOrder) return { key: 'none', label: 'Add items to start the order' };
  if (paid) return { key: 'close', label: 'Close table' };
  return { key: 'bill', label: 'Bill', amount: remaining };
};
